import { gsap } from "gsap";
import { lockScroll, unlockScroll } from "../../utils/Scroll";
import { openModalBackdrop, closeModalBackdrop, announceOverlay } from "../../utils/ModalBackdrop";
import { initSectionThemeTriggers } from "../../utils/sectionThemeTriggers";
import { mountTurnstile, unmountTurnstile, waitForTurnstile, resetTurnstile } from "../../utils/turnstile";

const EMAIL_RE = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g;
const ROOT = document.documentElement;
const FULLSCREEN_MQ = "(max-width: 999px)";

export default class ContactForm {
  constructor(el) {
    this.el = el;

    this.isOpen = false;
    this.validator = [false, false, false];

    this.init();
  }

  $(name) {
    return this.el.querySelectorAll(`[data-contact="${name}"]`);
  }

  init() {
    this.$window = this.$("window")[0];
    this.$form = document.querySelector(".c-contact_form");
    this.$mail = this.$("mail")[0];
    this.$subject = this.$("subject")[0];
    this.$message = this.$("message")[0];
    this.$errors = this.$("error");
    this.$submitButton = this.$("submitButton")[0];

    // The window translates itself off-screen while closed/animating, so its
    // own rect is meaningless — check the viewport centre instead, since
    // that's always where the window ends up once open.
    this.updateTheme = initSectionThemeTriggers(this.$window, {
      getY: () => window.innerHeight / 2,
    });

    this.bindCheckMail = this.checkMail.bind(this);
    this.bindCheckInput = this.checkInput.bind(this);
    this.bindKeydown = this.keydown.bind(this);
    this.bindOutsideClick = this.outsideClick.bind(this);
    this.bindContactLinkClick = this.contactLinkClick.bind(this);

    this.$mail.addEventListener("input", this.bindCheckMail);
    this.$subject.addEventListener("input", this.bindCheckInput);
    this.$message.addEventListener("input", this.bindCheckInput);

    this.$("close").forEach((btn) => {
      btn.addEventListener("click", () => this.toggle());
    });
    document.querySelectorAll(".c-footer__contactBtn").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggle();
      });
    });

    // Any link pointing to #contact opens the contact modal instead of navigating.
    document.addEventListener("click", this.bindContactLinkClick);
    this.$submitButton.addEventListener("click", (e) => this.submit(e));

    document.addEventListener("keydown", this.bindKeydown);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        ROOT.classList.add("has-contact-ready");
      });
    });
  }

  keydown(e) {
    if (this.isOpen && e.keyCode === 27) this.toggle();
  }

  contactLinkClick(e) {
    const link = e.target.closest("a");
    if (!link) return;

    const href = link.getAttribute("href");
    if (href !== "#contact" && !(href && href.endsWith("#contact"))) return;

    e.preventDefault();
    if (!this.isOpen) this.toggle();
  }

  outsideClick(e) {
    e.preventDefault();
    if (!this.$window.contains(e.target) && this.isOpen) this.toggle();
  }

  toggle() {
    if (this.isOpen) {
      ROOT.classList.remove("has-contact-open");
      unlockScroll();
      closeModalBackdrop(this);
      window.removeEventListener("click", this.bindOutsideClick);
      unmountTurnstile(this.$form);
    } else {
      // Full-screen window below 1000px (see _contact-form.scss): nothing
      // behind it to pixelate.
      if (window.matchMedia(FULLSCREEN_MQ).matches) announceOverlay(this);
      else openModalBackdrop(this);
      lockScroll();
      ROOT.classList.add("has-contact-open");
      mountTurnstile(this.$form);
      gsap.delayedCall(0.3, () => {
        window.addEventListener("click", this.bindOutsideClick);
      });
    }
    this.isOpen = !this.isOpen;
  }

  checkMail(e) {
    const value = e.currentTarget.value;
    let valid = false;

    if (value === "") {
      this.toggleError("notemail", false);
    } else {
      this.toggleError("empty", false);
      if (value.match(EMAIL_RE)) {
        this.toggleError("notemail", false);
        valid = true;
      } else {
        this.toggleError("notemail", true);
      }
    }

    this.setFieldState(this.$mail, valid);
    this.validator[0] = valid;
    this.validation();
  }

  checkInput(e) {
    const input = e.currentTarget;
    const index = input.getAttribute("name") === "contactSubject" ? 1 : 2;
    const valid = input.value.trim() !== "";

    this.toggleError("empty", !valid);

    this.setFieldState(input, valid);
    this.validator[index] = valid;
    this.validation();
  }

  // Recomputes every field's state from its current value. Used to gate submit
  // so an empty or malformed form can never reach the server.
  validateAll() {
    const mail = this.$mail.value.trim();
    const subject = this.$subject.value.trim();
    const message = this.$message.value.trim();

    const mailValid = mail !== "" && !!mail.match(EMAIL_RE);

    this.validator = [mailValid, subject !== "", message !== ""];

    this.setFieldState(this.$mail, mailValid);
    this.setFieldState(this.$subject, subject !== "");
    this.setFieldState(this.$message, message !== "");

    this.toggleError("empty", mail === "" || subject === "" || message === "");
    this.toggleError("notemail", mail !== "" && !mailValid);

    this.validation();
    return this.validator.every(Boolean);
  }

  resetForm() {
    this.$form.reset();
    this.validator = [false, false, false];
    [this.$mail, this.$subject, this.$message].forEach((input) => {
      input.parentNode.classList.remove("is-valid", "has-error");
    });
    this.toggleError("empty", false);
    this.toggleError("notemail", false);
    this.validation();
  }

  toggleError(message, active) {
    this.$errors.forEach((err) => {
      if (err.getAttribute("data-message") === message) {
        err.classList.toggle("is-active", active);
      }
    });
  }

  setFieldState(input, valid) {
    const parent = input.parentNode;
    parent.classList.toggle("is-valid", valid);
    parent.classList.toggle("has-error", !valid);
  }

  validation() {
    const allValid = this.validator[0] && this.validator[1] && this.validator[2];
    this.$submitButton.classList.toggle("is-valid", allValid);
  }

  submit(e) {
    e.preventDefault();
    if (ROOT.classList.contains("has-contact-pending")) return;
    if (!this.validateAll()) return;
    this.pending();
  }

  async pending() {
    ROOT.classList.add("has-contact-pending");

    // The widget also writes the token into a hidden input of the form, picked up below.
    await waitForTurnstile(this.$form);

    const actionInput = this.$form.querySelector('input[name="action"]');
    const ajaxUrl = (window.ajax_object && window.ajax_object.ajaxurl) || (window.vsAssets && window.vsAssets.ajaxUrl);
    const url = `${ajaxUrl}?action=${actionInput.value}`;

    fetch(url, {
      method: "POST",
      body: new FormData(this.$form),
    })
      .then((res) => res.json())
      .then((data) => {
        resetTurnstile(this.$form);
        gsap.delayedCall(2, () => {
          ROOT.classList.remove("has-contact-pending");
          if (data && data.success) {
            this.resetForm();
            this.success();
          } else {
            this.error(data && data.data && data.data.message);
          }
        });
      })
      .catch(() => {
        resetTurnstile(this.$form);
        gsap.delayedCall(2, () => {
          ROOT.classList.remove("has-contact-pending");
          this.error();
        });
      });
  }

  success() {
    ROOT.classList.add("has-contact-success");
    gsap.delayedCall(6, () => {
      ROOT.classList.remove("has-contact-success");
      this.toggle();
    });
  }

  error(message) {
    const node = this.el.querySelector(
      ".c-contact_notification.-error .c-contact_notification_message"
    );
    if (node) {
      node.textContent = message || "An error occured, retry later.";
    }
    ROOT.classList.add("has-contact-error");
    gsap.delayedCall(3, () => {
      ROOT.classList.remove("has-contact-error");
    });
  }

  destroy() {
    this.$mail.removeEventListener("input", this.bindCheckMail);
    this.$subject.removeEventListener("input", this.bindCheckInput);
    this.$message.removeEventListener("input", this.bindCheckInput);
    document.removeEventListener("keydown", this.bindKeydown);
    document.removeEventListener("click", this.bindContactLinkClick);
    window.removeEventListener("click", this.bindOutsideClick);
  }
}
